/**
 * The workspace's page tree for the sidebar (Favorites / Workspace / Private), the Projects hub and Home's recent
 * docs. Expansion is persisted per workspace; renames, favourites, moves and trash are optimistic and queued while
 * offline; creating a page (or a template such as "projects") needs the network.
 */
import type { CreatePageRequest, Locale, PageNode, PageTemplate, PageVisibility } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canCreateTopLevelPages } from "../logic/access.ts";
import { databases, keysToReveal, recent, rowKey, sectionOf, treeSections, type DocsCache, type PageTreeSection } from "../logic/docs.ts";
import { myFeatureAccess, type FeatureAccess } from "../logic/features.ts";
import type { LiveQuery } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type PageTreeState = {
  /** "loading" only until the first cached or fetched tree. */
  status: "loading" | "ready" | "error";
  sections: PageTreeSection[];
  /** Expanded row keys (`<section>/<pageId>`). */
  expanded: string[];
  /** Databases, most recently edited first (Projects hub). */
  databases: PageNode[];
  /** Recently edited pages and databases (Home). */
  recent: PageNode[];
  /** The tree itself, for lookups (tab titles, the omnibox, breadcrumbs). */
  cache?: DocsCache;
  /** A create request is in flight. */
  creating: boolean;
  /**
   * I may create top-level pages and boards (the Workspace / Private "+" and templates, "New project board"). False
   * for restricted members, who only add subpages to pages shared with them (rows' `editable`).
   */
  canCreatePages: boolean;
  /** The last create failed (usually offline). */
  error?: MessageDescriptor;
  /** Pages opened but not in the tree (database rows), for tab titles. */
  known: Record<string, PageNode>;
};

export type NewPage = {
  /** "page" (default) or "database"; ignored by templates that decide themselves ("projects"). */
  kind?: "page" | "database";
  template?: PageTemplate;
  parentId?: string;
  /** Top-level pages only (subpages inherit their parent's section). Default "workspace". */
  visibility?: PageVisibility;
  title?: string;
  icon?: string;
  afterId?: string;
  /** Templates: the language of their content (the creator's UI language). */
  locale?: Locale;
  /** Templates: leave the sample rows out. */
  startEmpty?: boolean;
};

const EXPANDED_PREFIX = "bundle.pages.expanded.";
const RECENT_LIMIT = 6;

export class PageTreeViewModel extends ViewModel<PageTreeState> {
  private readonly ws: WorkspaceViewModel;
  private readonly query: LiveQuery<DocsCache>;
  private readonly key: string;

  constructor(ws: WorkspaceViewModel) {
    const key = `${EXPANDED_PREFIX}${ws.wsId}`;
    super({ status: "loading", sections: [], expanded: PageTreeViewModel.readExpanded(ws, key), databases: [], recent: [], creating: false, canCreatePages: false, known: {} });
    this.ws = ws;
    this.key = key;
    this.query = ws.deps.data.pageTree(ws.wsId);
    this.onDispose(this.query.subscribe(() => this.recompute()));
    this.onDispose(() => this.query.dispose());
    this.onDispose(ws.subscribe(() => this.recomputeAccess()));
    this.recompute();
    this.recomputeAccess();
  }

  /** What I get of Docs (Settings → Features): `read` keeps the tree but nothing in it is editable. */
  private docs: FeatureAccess = "full";

  /** Follows my member record (role, access policy, expiry) and the workspace's switches in the workspace cache. */
  private recomputeAccess(): void {
    const cache = this.ws.state.cache;
    const now = this.ws.deps.clock.now();
    const me = cache === undefined ? undefined : cache.members[cache.meId];
    const docs = cache === undefined ? "full" : myFeatureAccess(cache, "docs", now);
    const changed = docs !== this.docs;
    this.docs = docs;
    this.update({ canCreatePages: me !== undefined && canCreateTopLevelPages(me, now) && docs === "full" });
    if (changed) this.recompute();
  }

  private static readExpanded(ws: WorkspaceViewModel, key: string): string[] {
    const raw = ws.deps.storage.get(key);
    if (raw === undefined) return [];
    try {
      const v: unknown = JSON.parse(raw);
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  }

  private recompute(expanded = this.state.expanded): void {
    const q = this.query.getSnapshot();
    const cache = q.data;
    if (cache === undefined) {
      this.update({ status: q.status === "error" ? "error" : "loading", expanded });
      return;
    }
    this.update({
      status: "ready",
      cache,
      expanded,
      sections: this.docs === "full" ? treeSections(cache, expanded, this.ws.state.meId) : lookOnly(treeSections(cache, expanded, this.ws.state.meId)),
      databases: databases(cache),
      recent: recent(cache, RECENT_LIMIT),
    });
  }

  private setExpanded(expanded: string[]): void {
    this.ws.deps.storage.set(this.key, JSON.stringify(expanded));
    this.recompute(expanded);
  }

  refresh(): Promise<void> {
    return this.query.refresh();
  }

  page(pageId: string): PageNode | undefined {
    return this.state.cache?.pages[pageId];
  }

  /** Remembers a page that isn't in the tree (a database row opened as a page) so tabs can name it. */
  remember(page: PageNode): void {
    if (this.state.cache?.pages[page.id] !== undefined) return;
    const old = this.state.known[page.id];
    if (old !== undefined && old.title === page.title && old.icon === page.icon) return;
    this.update({ known: { ...this.state.known, [page.id]: page } });
  }

  isFavorite(pageId: string): boolean {
    return this.state.cache?.favoriteIds.includes(pageId) ?? false;
  }

  /** Opens / closes a row's children. */
  toggle(key: string): void {
    const e = this.state.expanded;
    this.setExpanded(e.includes(key) ? e.filter((k) => k !== key) : [...e, key]);
  }

  /** Expands the ancestors of a page (and the page itself when `andSelf`) so it shows in the sidebar. */
  reveal(pageId: string, andSelf = false): void {
    const cache = this.state.cache;
    if (cache === undefined) return;
    const meId = this.ws.state.meId;
    const keys = keysToReveal(cache, pageId, meId);
    const p = cache.pages[pageId];
    if (andSelf && p !== undefined) keys.push(rowKey(sectionOf(cache, pageId, meId) ?? "workspace", pageId));
    const missing = keys.filter((k) => !this.state.expanded.includes(k));
    if (missing.length > 0) this.setExpanded([...this.state.expanded, ...missing]);
  }

  /**
   * Creates a page, a database or a template (online only). Resolves with the page to open, or undefined (and
   * `error` set) when the server can't be reached.
   */
  async create(input: NewPage = {}): Promise<PageNode | undefined> {
    const req: CreatePageRequest = {
      kind: input.kind ?? (input.template === "projects" ? "database" : "page"),
      clientId: this.ws.deps.ids.next(),
    };
    if (input.template !== undefined && input.template !== "blank") req.template = input.template;
    if (input.parentId !== undefined) req.parentId = input.parentId;
    else req.visibility = input.visibility ?? "workspace";
    if (input.title !== undefined) req.title = input.title;
    if (input.icon !== undefined) req.icon = input.icon;
    if (input.afterId !== undefined) req.afterId = input.afterId;
    if (input.locale !== undefined && req.template !== undefined) req.locale = input.locale;
    if (input.startEmpty === true && req.template !== undefined) req.startEmpty = true;
    const s = { ...this.state, creating: true };
    delete s.error;
    this.setState(s);
    try {
      const res = await this.ws.deps.data.createPage(this.ws.wsId, req);
      this.update({ creating: false });
      if (input.parentId !== undefined) this.reveal(res.page.id);
      return res.page;
    } catch {
      this.update({ creating: false, error: msg("docs.createFailed") });
      return undefined;
    }
  }

  dismissError(): void {
    if (this.state.error === undefined) return;
    const s = { ...this.state };
    delete s.error;
    this.setState(s);
  }

  rename(pageId: string, title: string): void {
    const p = this.page(pageId);
    if (p === undefined || p.title === title) return;
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { title });
  }

  /** An emoji (content) or "icon:<name>"; "" removes it. */
  setIcon(pageId: string, icon: string): void {
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { icon });
  }

  setFavorite(pageId: string, on: boolean): void {
    this.ws.deps.data.setFavorite(this.ws.wsId, pageId, on);
  }

  /** Moves the page (and its subtree) to the trash. */
  trash(pageId: string): void {
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { archived: true });
  }

  restore(pageId: string): void {
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { archived: false });
  }

  /** Moves a page under `parentId` (null ⇒ top level) after `afterId` (null ⇒ first). */
  move(pageId: string, parentId: string | null, afterId: string | null): void {
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { parentId, afterId });
    if (parentId !== null) this.reveal(pageId);
  }

  /** Moves a top-level page between Workspace and Private. */
  setVisibility(pageId: string, visibility: PageVisibility): void {
    this.ws.deps.data.updatePage(this.ws.wsId, pageId, { visibility, parentId: null });
  }
}

/** The tree while Docs is look-only for me: no row is editable (no New subpage, rename, move or delete). */
function lookOnly(sections: PageTreeSection[]): PageTreeSection[] {
  return sections.map((sec) => ({ ...sec, rows: sec.rows.map((r) => (r.editable ? { ...r, editable: false } : r)) }));
}
